import { useEffect } from 'react';

/**
 * Custom hook to dynamically hydrate and manage OpenGraph, Twitter Card,
 * canonical URLs, and structured JSON-LD schemas per route.
 */
export function useMetaTags({
  title,
  description,
  image,
  url,
  type = 'website',
  articleSchema = null,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) {
      document.title = title;
    }

    const updateMetaTag = (selector, attribute, value) => {
      if (!value) return null;
      let el = document.querySelector(selector);
      let created = false;
      let prevValue = null;
      if (!el) {
        el = document.createElement('meta');
        const cleanSelector = selector.replace(/^meta\[/, '').replace(/\]$/, '');
        const parts = cleanSelector.split('=');
        if (parts.length === 2) {
          const attrName = parts[0];
          const attrVal = parts[1].replace(/['"]/g, '');
          el.setAttribute(attrName, attrVal);
          document.head.appendChild(el);
          created = true;
        }
      } else {
        prevValue = el.getAttribute(attribute);
      }
      el.setAttribute(attribute, value);
      return { el, created, prevValue, attribute };
    };

    const restoredItems = [];

    // 1. Meta Description
    if (description) {
      restoredItems.push(updateMetaTag('meta[name="description"]', 'content', description));
      restoredItems.push(updateMetaTag('meta[property="og:description"]', 'content', description));
      restoredItems.push(updateMetaTag('meta[name="twitter:description"]', 'content', description));
    }

    // 2. OpenGraph & Twitter Titles
    if (title) {
      restoredItems.push(updateMetaTag('meta[property="og:title"]', 'content', title));
      restoredItems.push(updateMetaTag('meta[name="twitter:title"]', 'content', title));
    }

    // 3. Social Media Sharing Images
    if (image) {
      const fullImageUrl = image.startsWith('http') ? image : `https://ilovethisgame.ca${image}`;
      restoredItems.push(updateMetaTag('meta[property="og:image"]', 'content', fullImageUrl));
      restoredItems.push(updateMetaTag('meta[name="twitter:image"]', 'content', fullImageUrl));
    }

    // 4. URL & Canonical Links
    if (url) {
      const fullPageUrl = url.startsWith('http') ? url : `https://ilovethisgame.ca${url}`;
      restoredItems.push(updateMetaTag('meta[property="og:url"]', 'content', fullPageUrl));

      const canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) {
        const prevHref = canonicalEl.getAttribute('href');
        canonicalEl.setAttribute('href', fullPageUrl);
        restoredItems.push({ el: canonicalEl, created: false, prevValue: prevHref, attribute: 'href' });
      }
    }

    // 5. OpenGraph Type
    if (type) {
      restoredItems.push(updateMetaTag('meta[property="og:type"]', 'content', type));
    }

    // 6. Dynamic JSON-LD Structured Schema
    let dynamicSchemaEl = null;
    if (articleSchema) {
      dynamicSchemaEl = document.createElement('script');
      dynamicSchemaEl.type = 'application/ld+json';
      dynamicSchemaEl.id = 'dynamic-route-schema';
      dynamicSchemaEl.textContent = typeof articleSchema === 'string' ? articleSchema : JSON.stringify(articleSchema);
      document.head.appendChild(dynamicSchemaEl);
    }

    return () => {
      document.title = prevTitle;
      restoredItems.forEach(item => {
        if (!item || !item.el) return;
        if (item.created) {
          item.el.remove();
        } else if (item.prevValue !== null) {
          item.el.setAttribute(item.attribute, item.prevValue);
        }
      });
      if (dynamicSchemaEl) {
        dynamicSchemaEl.remove();
      }
    };
  }, [title, description, image, url, type, articleSchema]);
}
